import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { HeartHandshake } from 'lucide-react';
import StatusPill from '../components/StatusPill';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
const ENQ_STATUSES = ['new', 'in_review', 'quoted', 'converted', 'closed'];

export default function ServiceEnquiries() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [statusChange, setStatusChange] = useState('');
    const [notes, setNotes] = useState('');

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/service-enquiries'); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openDetail = (e) => { setSelected(e); setStatusChange(e.status); setNotes(e.notes || ''); };

    const save = async () => {
        try { await api.patch(`/service-enquiries/${selected.id}`, { status: statusChange, notes });
            toast.success('Updated'); setSelected(null); load();
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    return (
        <AppShell pageTitle="Service Enquiries">
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={HeartHandshake} title="No enquiries yet" description="Service enquiries from customers will appear here." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="enquiries-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Name</th>
                                <th className="text-left font-medium">Mobile</th>
                                <th className="text-left font-medium">Service</th>
                                <th className="text-left font-medium">Location</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-left p-3 font-medium">Received</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(e => (
                                <tr key={e.id} className="border-b hover:bg-neutral-50 cursor-pointer" onClick={() => openDetail(e)} data-testid={`enquiry-row-${e.id}`}>
                                    <td className="p-3 font-medium">{e.name}</td>
                                    <td className="font-mono text-xs">{e.mobile}</td>
                                    <td className="text-xs">{e.service_type}</td>
                                    <td className="text-xs">{e.location}</td>
                                    <td><StatusPill status={e.status} /></td>
                                    <td className="p-3 text-xs text-neutral-600">{new Date(e.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Enquiry — {selected?.name}</DialogTitle></DialogHeader>
                    {selected && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div><span className="text-neutral-500">Mobile:</span> {selected.mobile}</div>
                                <div><span className="text-neutral-500">Email:</span> {selected.email || '—'}</div>
                                <div><span className="text-neutral-500">Service:</span> {selected.service_type}</div>
                                <div><span className="text-neutral-500">Location:</span> {selected.location || '—'}</div>
                                <div><span className="text-neutral-500">Budget:</span> {selected.budget || '—'}</div>
                            </div>
                            {selected.message && (
                                <div className="p-3 rounded bg-neutral-50 text-sm"><span className="font-medium">Message:</span><br />{selected.message}</div>
                            )}
                            <div>
                                <Label>Change Status</Label>
                                <Select value={statusChange} onValueChange={setStatusChange}>
                                    <SelectTrigger data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{ENQ_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Notes</Label><Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} data-testid="enquiry-notes-input" /></div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                        <Button onClick={save} data-testid="save-enquiry-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
